// KPI Builder — shared types. Pure TypeScript, no React or API imports.

export type RecipeId = 'count' | 'perGroup' | 'trend' | 'rate' | 'gap' | 'list' | 'compare' | 'share' | 'funnel' | 'questions' | 'template' | 'ranges' | 'progress' | 'ready' | 'advanced';
export type SourceId = 'reg' | 'enr' | 'pass' | 'rating' | 'assess' | 'question' | 'activity' | 'custom';
export type QuestionKind = 'difficulty' | 'cohort';
export type ReadyKind = 'dropout' | 'reattempt' | 'attempts' | 'facility' | 'histogram' | 'levelFunnel' | 'completedFunnel';
export type ProgressKind = 'coverage' | 'quality' | 'course';
export type Interval = 'day' | 'week' | 'month';
export type TabId = 'info' | 'data' | 'display' | 'filters' | 'access' | 'dashboards';

/** One step of a funnel: an existing KPI, and optionally the KPI that lists who dropped out at this step. */
export interface FunnelStage {
  /** Code of an existing visualization. */
  kpi: string;
  label: string;
  gapKpi: string;
  gapLabel: string;
  /** Text after the percentage on the next stage, e.g. “of Registered Professionals Enrolled”. */
  pill: string;
}

export interface FunnelAnswers {
  /** 'stage': steps in a row. 'conversion': one starting KPI and several outcomes. */
  kind: 'stage' | 'conversion';
  stages: FunnelStage[];
}

export type TemplateCadre = 'keep' | 'inService' | 'inServiceAnm' | 'preService';

/** An existing KPI copied as the starting point: its stored query and config, plus what to change. */
export interface TemplateAnswers {
  code: string;
  title: string;
  chartType: string;
  handlerKey: string;
  /** The stored query array (a JSON string), exactly as the KPI has it. */
  query: string;
  /** The stored config (a JSON string). */
  config: string;
  cadre: TemplateCadre;
}

/** Everything the admin chooses in the Data, Additional Config and KPI Filters tabs. */
export interface BuilderAnswers {
  recipe: RecipeId | null;
  src: SourceId | null;
  /** Second list for the "in A but not in B" recipe only. */
  srcB: SourceId | null;
  /** Ticked learner-group ids. */
  segs: string[];
  measure: string | null;
  group: string | null;
  topN: number;
  /** Only keep groups with at least this many records (1 keeps everything). */
  minCount: number;
  interval: Interval;
  cumulative: boolean;
  /** Learner group used as the numerator of the "% per group" recipe. */
  rateSeg: string | null;
  /** List recipe columns. */
  cols: string[];
  rows: number;
  funnel: FunnelAnswers;
  /** Which analysis the “questions” recipe runs. */
  questionKind: QuestionKind;
  /** Which learning-progress comparison the “progress” recipe runs. */
  progressKind: ProgressKind;
  /** Which ready-made chart the “ready” recipe builds. */
  readyKind: ReadyKind;
  /** Lowest non-completion rate (%) the facility chart shows. */
  threshold: number;
  extras: ExtraAnswers;
  /** The queries of a KPI built part by part. */
  advanced: AdvancedAnswers;
  template: TemplateAnswers;
  custom: CustomAnswers;
  chart: string | null;
  kpiFilters: string[];
  drill: string;
}

export const EMPTY_ANSWERS: BuilderAnswers = {
  recipe: null, src: null, srcB: null, segs: [], measure: null, group: null, topN: 10, minCount: 1,
  interval: 'day', cumulative: false, rateSeg: null, cols: ['name', 'district'], rows: 1000,
  funnel: { kind: 'stage', stages: [] },
  questionKind: 'difficulty',
  progressKind: 'coverage',
  readyKind: 'dropout',
  threshold: 70,
  extras: { handlerKey: '', filters: [], settings: [] },
  advanced: { queries: [] },
  template: { code: '', title: '', chartType: '', handlerKey: '', query: '', config: '', cadre: 'keep' },
  custom: {
    source: { label: '', index: '', date: '', comp: '', hasLocation: true },
    measure: { agg: 'cardinality', field: 'userId', label: 'Unique learners', divisor: 1 },
    group: { field: '', label: '' }, filters: [], columns: [], rangeField: '', ranges: [], buckets: [], extraSeries: [],
    rateWhen: { field: '', op: 'is', value: '', value2: '' }, rateLabel: '', firstBar: '', secondBar: '',
  },
  chart: null, kpiFilters: [], drill: 'none',
};

export type MetricAgg = 'cardinality' | 'value_count' | 'avg' | 'sum' | 'min' | 'max';
export interface Measure { id: string; label: string; desc: string; agg: MetricAgg; field: string; decimal?: boolean; /** Divide the value, e.g. 60000 to show milliseconds as minutes. */ divisor?: number; }
export interface GroupBy { id: string; label: string; field: string; cat: string; }
/** How a list column is worked out: the value, how many different values, or the latest date. */
export type ColumnKind = 'value' | 'count' | 'date';
export interface Column { id: string; label: string; field?: string; script?: boolean; pii?: boolean; kind?: ColumnKind; }

export interface FieldDef { field: string; label: string; }

export type FilterOp = 'is' | 'isNot' | 'oneOf' | 'exists' | 'missing' | 'gt' | 'gte' | 'lt' | 'lte' | 'between';
/** One condition the admin typed: a field, how to compare it, and the value(s). */
export interface CustomFilter { field: string; op: FilterOp; value: string; value2: string; }
/** Data the admin named themselves instead of choosing from the catalogue. */
export interface CustomSource { label: string; index: string; date: string; comp: string; hasLocation: boolean; }
export interface CustomMeasure { agg: MetricAgg; field: string; label: string; divisor: number; }
export interface CustomGroup { field: string; label: string; }
export interface CustomColumn { label: string; field: string; kind?: ColumnKind; }
export interface RangeBucket { label: string; from: string; to: string; }
/** A named bucket the admin defines: records matching the condition fall into it. */
export interface CustomBucket { label: string; when: CustomFilter; }

/** Everything typed in by the admin: any field, any condition, any data. The catalogue only supplies suggestions. */
export interface CustomAnswers {
  source: CustomSource;
  measure: CustomMeasure;
  group: CustomGroup;
  /** Extra conditions on the main data, on top of the learner groups. */
  filters: CustomFilter[];
  columns: CustomColumn[];
  rangeField: string;
  ranges: RangeBucket[];
  /** Buckets for the “split into named buckets” question. When empty, the data's own buckets are used. */
  buckets: CustomBucket[];
  /** More bars for the side-by-side question, after “everyone” and the trait chosen to compare against. */
  extraSeries: CustomBucket[];
  /** The condition for “percentage of…” and “compare against”, when it is not a saved learner group. */
  rateWhen: CustomFilter;
  rateLabel: string;
  /** Names for the first two bars of the side-by-side question. Blank keeps the automatic name. */
  firstBar: string;
  secondBar: string;
}

export interface DataSource {
  id: SourceId; label: string; desc: string; index: string; date: string;
  /** Competency id field name in this index (spelling differs between indices). */
  comp?: string;
  /** Word used in titles: "registered", "enrolled"… */
  word: string;
  measures: Measure[]; groups: GroupBy[]; cols: Column[];
  /** True when the data has no district, block or facility fields to filter by. */
  noLocation?: boolean;
  /** Named buckets for the share recipe, e.g. 1 to 5 stars. */
  buckets?: { label: string; field: string; value: string | number | boolean }[];
}

export interface LearnerGroup {
  id: string; label: string; rule: string; applies: SourceId[];
  field?: string; values?: (string | boolean | number)[];
  /** Uses the active-competency terms lookup instead of field/values. */
  lookup?: boolean;
  preService?: boolean;
  /** Short word for chart legends, e.g. “Passed”. Falls back to the label. */
  short?: string;
}

export interface ChartOption { id: string; label: string; desc: string; }
/** `answers`, `youGet` and `example` explain the question to the admin: what it answers, what they will get, and one example. */
export interface Recipe { id: RecipeId; label: string; desc: string; answers: string; youGet: string; example: string; ready: boolean; sources: SourceId[]; }

export interface KpiFilterDef { id: string; label: string; desc: string; key: string; field?: string; applies: SourceId[]; def: Record<string, unknown>; }

export interface Issue {
  tab: TabId; level: 'error' | 'warning'; title: string; msg: string;
  /** Optional one-click fix: a patch to apply to the answers (or to roles). */
  fix?: { label: string; answers?: Partial<BuilderAnswers>; roles?: string[] };
}

/** A node of an Elasticsearch aggregation tree. */
export interface EsNode {
  aggs?: Record<string, EsNode>;
  [key: string]: unknown;
}

export interface AggrQuery {
  size?: number;
  aggs: Record<string, EsNode>;
}

/** One aggregation: its name, its type (terms, filter, range, avg_bucket...), the JSON of its settings, and the aggregations inside it. */
export interface AggNode { name: string; type: string; body: string; children: AggNode[]; }
/** One query of a hand-built KPI, with every part editable. `top` is the JSON of the keys next to the aggregations, such as {"size":0}. */
export interface AdvancedQuery { index: string; date: string; filterPath: string; top: string; map: ExtraFilter[]; aggs: AggNode[]; }
export interface AdvancedAnswers { queries: AdvancedQuery[]; }

/** A dashboard filter added by hand: the filter's name on the dashboard, and the field it narrows. `on` is the query it applies to (1, 2, ...), blank for every query. */
export interface ExtraFilter { key: string; field: string; on: string; }
/** A setting added to the config by hand. The value is read as JSON (a number, true/false, a list...), otherwise kept as text. */
export interface ExtraSetting { key: string; value: string; }
/** What hand-written JSON can add to any KPI, on top of what the questions write. */
export interface ExtraAnswers { handlerKey: string; filters: ExtraFilter[]; settings: ExtraSetting[]; }

/** One element of the stored `query` array, before stringification. */
export interface QueryObject {
  module: 'COMMON'; dataSource: 'ES'; esInstance: 'QUADNARY';
  requestQueryMap: Record<string, string>; dateRefField: string; indexName: string;
  filterPath?: string; aggrQuery: AggrQuery;
}

/** The answers saved inside the config so a builder KPI can be reopened. */
export interface BuilderSnapshot {
  recipe: RecipeId | null; source: SourceId | null; sourceB: SourceId | null; learnerGroups: string[];
  measure: string | null; groupBy: string | null; topN: number; minCount: number; interval: Interval; cumulative: boolean;
  rateGroup: string | null; columns: string[]; rows: number; kpiFilters: string[]; funnel?: FunnelAnswers; questionKind?: QuestionKind; progressKind?: ProgressKind; readyKind?: ReadyKind; threshold?: number; extras?: ExtraAnswers; advanced?: AdvancedAnswers; custom?: CustomAnswers;
  /** Which KPI was copied and how, without its query: the original is looked up again when the copy is reopened. */
  template?: Omit<TemplateAnswers, 'query' | 'config'>;
  /** The chart the admin picked. config.chartType can differ: a trend drawn as bars still tells the backend 'line'. */
  chart?: string;
}

/** additionalConfig.config as generated by the builder. */
export interface BuilderConfig {
  chartType: string;
  drillChart: string;
  aggregationPaths: string[];
  kpiFilters?: Record<string, unknown>[];
  builder: BuilderSnapshot;
  [key: string]: unknown;
}

/** The click-through list a gap KPI needs; saved as a second visualization. */
export interface DetailOutput {
  title: string;
  visualizationCode: string;
  chartType: string;
  queryString: string;
  config: Record<string, unknown>;
}

export interface BuilderOutput {
  visualizationCode: string;
  chartType: string;
  /** Backend handler this visualization needs, when its recipe is handler-based. */
  handlerKey?: string;
  /** Query array exactly as the backend stores it: requestQueryMap and aggrQuery are JSON strings. */
  queryString: string;
  /** additionalConfig.config as an object. */
  config: BuilderConfig;
  /** Readable form, for the developer view only. */
  queries: QueryObject[];
  /** Only the gap recipe has one: the list opened when its number is clicked. */
  detail?: DetailOutput;
}
